export class InMemoryDeviceKeyStore {
    key;
    load() {
        return Promise.resolve(this.key);
    }
    save(key) {
        this.key = key;
        return Promise.resolve();
    }
    saveIfAbsent(candidate) {
        this.key ??= candidate; // synchronous check-and-set: atomic within this JS context
        return Promise.resolve(this.key);
    }
    clear() {
        this.key = undefined;
        return Promise.resolve();
    }
}
const requestResult = (r) => new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('indexedDB request failed'));
});
const transactionDone = (tx) => new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('indexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('indexedDB transaction aborted'));
});
function isP256PublicJwk(v) {
    if (typeof v !== 'object' || v === null)
        return false;
    const j = v;
    return j.kty === 'EC' && j.crv === 'P-256' && typeof j.x === 'string' && j.x !== '' && typeof j.y === 'string' && j.y !== '';
}
/** IndexedDB-backed key store. The `CryptoKey` is stored by structured clone — never exported. */
export class IndexedDbDeviceKeyStore {
    options;
    dbName;
    storeName;
    recordKey;
    constructor(options = {}) {
        this.options = options;
        this.dbName = options.dbName ?? 'vello-device-keys';
        this.storeName = options.storeName ?? 'keys';
        this.recordKey = options.recordKey ?? 'device-proof-v1';
    }
    open() {
        const factory = this.options.factory ?? globalThis.indexedDB;
        if (factory === undefined)
            return Promise.reject(new Error('indexedDB is unavailable'));
        return new Promise((resolve, reject) => {
            const req = factory.open(this.dbName, 1);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(this.storeName))
                    db.createObjectStore(this.storeName);
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
        });
    }
    async load() {
        const db = await this.open();
        try {
            const tx = db.transaction(this.storeName, 'readonly');
            const value = await requestResult(tx.objectStore(this.storeName).get(this.recordKey));
            if (typeof value !== 'object' || value === null)
                return undefined;
            const v = value;
            return v.privateKey !== undefined && v.privateKey !== null && isP256PublicJwk(v.publicJwk) ? { privateKey: v.privateKey, publicJwk: v.publicJwk } : undefined;
        }
        finally {
            db.close();
        }
    }
    async save(key) {
        const db = await this.open();
        try {
            const tx = db.transaction(this.storeName, 'readwrite');
            const done = transactionDone(tx);
            tx.objectStore(this.storeName).put({ privateKey: key.privateKey, publicJwk: key.publicJwk }, this.recordKey);
            await done;
        }
        finally {
            db.close();
        }
    }
    async clear() {
        const db = await this.open();
        try {
            const tx = db.transaction(this.storeName, 'readwrite');
            const done = transactionDone(tx);
            tx.objectStore(this.storeName).delete(this.recordKey);
            await done;
        }
        finally {
            db.close();
        }
    }
    /**
     * Get-or-create inside ONE readwrite transaction. IndexedDB runs readwrite transactions over the same object store
     * one at a time across every connection of the origin (all tabs), and the `put` is issued from the `get`'s success
     * callback while the transaction is still active — so exactly one context's candidate is persisted and every
     * context reads back that winner.
     */
    async saveIfAbsent(candidate) {
        const db = await this.open();
        try {
            const tx = db.transaction(this.storeName, 'readwrite');
            const done = transactionDone(tx);
            const store = tx.objectStore(this.storeName);
            let winner = candidate;
            const get = store.get(this.recordKey);
            get.onsuccess = () => {
                const v = get.result;
                const existing = typeof v === 'object' && v !== null ? v : undefined;
                if (existing !== undefined && existing.privateKey !== undefined && existing.privateKey !== null && isP256PublicJwk(existing.publicJwk)) {
                    winner = { privateKey: existing.privateKey, publicJwk: existing.publicJwk };
                }
                else {
                    store.put({ privateKey: candidate.privateKey, publicJwk: candidate.publicJwk }, this.recordKey);
                }
            };
            get.onerror = () => undefined; // surfaces through the transaction's error/abort
            await done;
            return winner;
        }
        finally {
            db.close();
        }
    }
}
/**
 * Create the web signer. The key is generated lazily on first use (`extractable: false`), loaded from the key store
 * when present, and memoized for the page lifetime. If the key store is unavailable (private mode, blocked storage)
 * the signer still works with an in-memory key for this page — the binding then lasts until the next reload.
 */
export function createWebCryptoDeviceSigner(options = {}) {
    const keyStore = options.keyStore ?? new IndexedDbDeviceKeyStore();
    const resolveSubtle = () => {
        const subtle = options.subtle ?? globalThis.crypto?.subtle;
        if (subtle === undefined)
            throw new Error('WebCrypto is unavailable in this runtime');
        return subtle;
    };
    let pending;
    const ensureKey = () => {
        pending ??= (async () => {
            let stored;
            try {
                stored = await keyStore.load();
            }
            catch {
                stored = undefined;
            }
            if (stored !== undefined)
                return stored;
            const subtle = resolveSubtle();
            const pair = (await subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']));
            if (pair.privateKey === undefined || pair.publicKey === undefined)
                throw new Error('WebCrypto did not return an ECDSA key pair');
            const exported = await subtle.exportKey('jwk', pair.publicKey);
            const raw = (typeof exported === 'object' && exported !== null ? exported : {});
            const publicJwk = { kty: 'EC', crv: 'P-256', x: raw.x, y: raw.y };
            if (!isP256PublicJwk(publicJwk))
                throw new Error('WebCrypto exported an unexpected public key');
            const candidate = { privateKey: pair.privateKey, publicJwk };
            // Another context may be creating a key at this very moment: persist atomically and sign ONLY with the winner,
            // so a session bound at login in one tab is refreshable from every tab (and after a reload).
            try {
                if (keyStore.saveIfAbsent !== undefined)
                    return await keyStore.saveIfAbsent(candidate);
                await keyStore.save(candidate);
                return (await keyStore.load()) ?? candidate;
            }
            catch {
                return candidate; // storage unavailable: this key signs for the current page lifetime only
            }
        })().catch((e) => {
            pending = undefined; // allow a retry on the next call
            throw e;
        });
        return pending;
    };
    return {
        async publicJwk() {
            return (await ensureKey()).publicJwk;
        },
        async sign(signingInput) {
            const key = await ensureKey();
            return new Uint8Array(await resolveSubtle().sign({ name: 'ECDSA', hash: 'SHA-256' }, key.privateKey, signingInput));
        },
        async reset() {
            pending = undefined;
            try {
                await keyStore.clear();
            }
            catch {
                /* nothing persisted to clear */
            }
        },
    };
}
//# sourceMappingURL=web-device-signer.js.map